import { Theme } from '@mui/material/styles'
import { focusRing } from './Focus'

// ----------------------------------------------------------------------

export default function Input(theme: Theme) {
  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            '& svg': {
              color: theme.palette.text.disabled,
            },
          },
        },
        input: {
          '&::placeholder': {
            opacity: 1,
            color: theme.palette.text.disabled,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          // A hairline, not a fill — use the divider token so it matches the
          // same warm line used elsewhere instead of a cool grey alpha.
          '&:before': {
            borderBottomColor: theme.palette.divider,
          },
          '&:after': {
            borderBottomColor: theme.palette.text.primary,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          width: '100%',
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.text.primary,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          /**
           * Rest fill matches the warm sand `background.neutral` used by rows
           * and panels around the input (e.g. the widget editor), instead of
           * `alpha(grey[500], .16)`, which is a cool grey and reads as a
           * different surface next to it. Hover/focus step one shade darker
           * on the same sand ramp (`grey[300]`) rather than a heavier alpha of
           * the old cool grey, so the whole rest/hover/focus sequence stays on
           * one warm ramp instead of mixing a warm rest with a cool hover.
           */
          backgroundColor: theme.palette.background.neutral,
          '&:hover': {
            backgroundColor: theme.palette.grey[300],
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.grey[300],
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          '.MuiSelect-select.MuiInputBase-inputHiddenLabel': {
            paddingTop: 10,
            paddingBottom: 11,
          },
          // Lab input height ≈ 34-36px: one padding step tighter than the
          // MUI filled default, matched by the hidden-label select above.
          '& .MuiFilledInput-input': {
            paddingTop: 21,
            paddingBottom: 6,
          },
          '& .MuiFilledInput-input.MuiInputBase-inputHiddenLabel': {
            paddingTop: 10,
            paddingBottom: 11,
          },
        },
        underline: {
          '&:before, :after': {
            display: 'none',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Same hairline intent as MuiInput's underline above — the divider
          // token, not a cool grey alpha, so the outline reads as the same
          // line used elsewhere rather than a third, cooler grey.
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&.Mui-focused': {
            ...focusRing(theme),
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: theme.palette.text.primary,
            },
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
          '& .MuiOutlinedInput-input': {
            paddingTop: 12,
            paddingBottom: 12,
          },
        },
      },
    },
  }
}
